import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  alpha,
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  Fade,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Email,
  InsightsOutlined,
  LockOutlined,
  Login as LoginIcon,
  ShieldOutlined,
  TimelineOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import BrandIcon from "../../components/common/BrandIcon";
import SurfaceCard from "../../components/common/ui/SurfaceCard";
import {
  FORGOT_PASSWORD_ROUTE_PATH,
  HOME_ROUTE_PATH,
  REGISTER_ROUTE_PATH,
  VERIFY_EMAIL_ROUTE_PATH,
} from "../../utils/constants/AppConstants";
import { useError } from "../../error/useError";
import ErrorAlert from "../../components/common/ErrorAlert";
import { useAuth } from "../../auth/AuthContext";

type LocationState = { message?: string } | null;

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const state = (location.state as LocationState) || null;
  const [infoMessage, setInfoMessage] = useState<string | null>(state?.message || null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const REMEMBER_ME_PREF_KEY = "remember-me";
  const [extendedSession, setExtendedSession] = useState(() => {
    try {
      const pref = localStorage.getItem(REMEMBER_ME_PREF_KEY);
      return pref ? pref === "true" : false;
    } catch (e) {
      return false;
    }
  });

  const { error, helpText, isLoading, clearError, handleError, setLoading } = useError();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      setLoading(true);
      clearError();
      const result = await login(email, password, extendedSession);
      if (result.emailVerified) {
        navigate(HOME_ROUTE_PATH);
      } else {
        navigate(VERIFY_EMAIL_ROUTE_PATH, { state: { email: result.email } });
      }
    } catch (e: any) {
      handleError(e, "Login failed. Please check your credentials and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword((show) => !show);
  };

  useEffect(() => {
    if (state?.message) {
      window.history.replaceState({}, document.title);
    }
  }, [state]);

  const inputSx = {
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
    "& .MuiOutlinedInput-root": {
      background: alpha(theme.palette.background.paper, 0.6),
      borderRadius: theme.radii?.medium ?? 10,
      color: theme.palette.text.primary,
      transition: "all 0.22s ease",
      "& input": { color: theme.palette.text.primary },
      "& input:-webkit-autofill": {
        WebkitTextFillColor: theme.palette.text.primary,
        caretColor: theme.palette.text.primary,
        WebkitBoxShadow: `0 0 0 100px ${alpha(theme.palette.background.paper, 0.6)} inset`,
        borderRadius: theme.radii?.medium ?? 10,
        transition: "background-color 9999s ease-out, color 9999s ease-out",
      },
      "& input:-webkit-autofill:hover": {
        WebkitTextFillColor: theme.palette.text.primary,
      },
      "& fieldset": { borderColor: alpha(theme.palette.text.primary, 0.1) },
      "&:hover fieldset": { borderColor: alpha(theme.palette.text.primary, 0.22) },
      "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main, borderWidth: 2 },
      "&.Mui-focused": { boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.2)}` },
      "&.Mui-focused input:-webkit-autofill": {
        WebkitBoxShadow: `0 0 0 100px ${alpha(theme.palette.background.paper, 0.72)} inset`,
      },
    },
  };

  const leftPaneHighlights = [
    { icon: <InsightsOutlined fontSize="small" />, text: "Live monthly insights" },
    { icon: <TimelineOutlined fontSize="small" />, text: "Fast, focused financial workflows" },
    { icon: <ShieldOutlined fontSize="small" />, text: "Secure account and session controls" },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `radial-gradient(circle at 18% 16%, ${alpha(theme.palette.primary.main, 0.16)} 0%, transparent 38%), ${theme.palette.background.default}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="lg">
        <Fade in={true} timeout={800}>
          <SurfaceCard
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
              background: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: "blur(8px)",
              boxShadow: "0 14px 44px rgba(2, 6, 23, 0.55)",
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  p: { xs: 2.6, sm: 3.2, md: 3.4 },
                  borderRight: { md: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` },
                  borderBottom: { xs: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`, md: "none" },
                  background: `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.background.paper, 0.35)})`,
                }}
              >
                <Box sx={{ mb: 2.2 }}>
                  <BrandIcon size={48} />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                  }}
                >
                  Welcome back
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8, maxWidth: 360 }}>
                  Sign in to continue tracking your expenses, income, and monthly trends in one place.
                </Typography>

                <Stack spacing={1} sx={{ mt: 2.2 }}>
                  {leftPaneHighlights.map((item) => (
                    <Stack key={item.text} direction="row" spacing={1} alignItems="center">
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                          color: theme.palette.primary.light,
                        }}
                      >
                        {item.icon}
                      </Avatar>
                      <Typography variant="body2" color="text.secondary">
                        {item.text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Box
                  sx={{
                    mt: 2.4,
                    borderRadius: "12px",
                    backgroundColor: alpha(theme.palette.primary.main, 0.14),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                    px: 1.3,
                    py: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: theme.palette.primary.light, lineHeight: 1.35 }}>
                    First time here? Create your account in under a minute.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={7} sx={{ p: { xs: 2.6, sm: 3.2, md: 3.8 } }}>
                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2.2 }}>
                  <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                    <LockOutlined fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      Sign In
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Access your PocketBalance account
                    </Typography>
                  </Box>
                </Stack>

                {infoMessage && (
                  <Alert
                    severity="success"
                    onClose={() => setInfoMessage(null)}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      background: alpha(theme.palette.success.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.16)}`,
                    }}
                  >
                    {infoMessage}
                  </Alert>
                )}

                <ErrorAlert
                  error={error}
                  helpText={helpText}
                  onClear={clearError}
                  title="Login Failed"
                  showRetry={false}
                />

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1.8 }}>
                  <TextField
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    sx={{ mb: 2.4, ...inputSx }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    name="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    sx={{ mb: 2.1, ...inputSx }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                            disabled={isLoading}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.3, gap: 1, flexWrap: "wrap" }}>
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Checkbox
                          id="remember-me"
                          checked={extendedSession}
                          onChange={(e) => {
                            setExtendedSession(e.target.checked);
                            try {
                              localStorage.setItem(REMEMBER_ME_PREF_KEY, String(e.target.checked));
                            } catch (err) {
                              // ignore storage errors
                            }
                          }}
                          disabled={isLoading}
                          color="primary"
                          size="small"
                        />
                      }
                      label="Keep me signed in"
                    />

                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => navigate(FORGOT_PASSWORD_ROUTE_PATH)}
                      sx={{ color: theme.palette.primary.main, whiteSpace: "nowrap", fontWeight: 600 }}
                    >
                      Forgot password?
                    </Link>
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                    sx={{
                      py: 1.35,
                      mb: 2.3,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      color: theme.palette.primary.contrastText,
                      borderRadius: theme.radii?.medium ?? 10,
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.28)}`,
                      },
                      "&:disabled": {
                        background: theme.palette.action.disabledBackground,
                      },
                      transition: "all 0.22s cubic-bezier(.2,.9,.2,1)",
                    }}
                  >
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>

                  <Divider sx={{ my: 2.2 }}>
                    <Typography variant="body2" color="text.secondary">
                      New to PocketBalance?
                    </Typography>
                  </Divider>

                  <Box sx={{ textAlign: "center" }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body1"
                      onClick={() => navigate(REGISTER_ROUTE_PATH)}
                      sx={{
                        textDecoration: "none",
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        "&:hover": {
                          color: theme.palette.primary.light,
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Create your free account
                    </Link>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.8 }}>
                      No credit card required.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </SurfaceCard>
        </Fade>
      </Container>
    </Box>
  );
};

export default LoginPage;
